import { GetContract } from "@/services/fhir/contract/GetContract";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useOrganizationContext } from "./OrganizationContext";
import { Contract } from "fhir/r5";

type ContractResource = Contract;

interface SecretaryAgentContextValue {
  contractSecretaryAgent: ContractResource | null;
  provider: "openai" | "gemini" | "anthropic" | null;
  apiKey: string | null;
  refetchContract: () => Promise<void>;
}

interface SecretaryAgentContextProviderProps {
  children: React.ReactNode;
}

export const SecretaryAgentContext = createContext<SecretaryAgentContextValue>(
  {} as SecretaryAgentContextValue
);

/**
 * Contrato FHIR da extensão Secretária IA (`SECRETARY_AGENT`).
 * Mesmo padrão dos demais agentes: status executed + provider/api-key nas extensions.
 */
export function SecretaryAgentContextProvider({
  children,
}: SecretaryAgentContextProviderProps) {
  const { organization } = useOrganizationContext();
  const [contractSecretaryAgent, setContractSecretaryAgent] =
    useState<ContractResource | null>(null);
  const [provider, setProvider] = useState<
    "openai" | "gemini" | "anthropic" | null
  >(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  const fetchContract = useCallback(async () => {
    try {
      if (!organization?.id) {
        console.warn("Nenhuma organização selecionada");
        setContractSecretaryAgent(null);
        setProvider(null);
        setApiKey(null);
        return;
      }

      const contractData = await GetContract({
        organization: organization.id,
        count: 1,
        sort: true,
        status: "executed",
        identifier: "SECRETARY_AGENT",
      });

      if (contractData?.entry?.[0]?.resource) {
        const resource = contractData.entry[0].resource;
        setContractSecretaryAgent(resource);

        const apiKeyExtension = resource.extension?.find(
          (ext: any) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/extension/secretary_agent/api-key"
        );
        const providerExtension = resource.extension?.find(
          (ext: any) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/extension/secretary_agent/provider"
        );

        if (apiKeyExtension?.valueString && providerExtension?.valueString) {
          setProvider(
            providerExtension.valueString as "openai" | "gemini" | "anthropic"
          );
          setApiKey(apiKeyExtension.valueString);
        } else {
          setProvider(null);
          setApiKey(null);
        }
      } else {
        setContractSecretaryAgent(null);
        setProvider(null);
        setApiKey(null);
      }
    } catch (error) {
      console.error("Erro ao buscar contrato:", error);
      setContractSecretaryAgent(null);
      setProvider(null);
      setApiKey(null);
    }
  }, [organization]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const contextValue: SecretaryAgentContextValue = {
    contractSecretaryAgent,
    provider,
    apiKey,
    refetchContract: fetchContract,
  };

  return (
    <SecretaryAgentContext.Provider value={contextValue}>
      {children}
    </SecretaryAgentContext.Provider>
  );
}

export function useSecretaryAgentContext() {
  return useContext(SecretaryAgentContext);
}
